import { Box, IconButton } from '@mui/material';
import { ChevronDown, ChevronRight, GripVertical, X } from 'lucide-react';
import {
    KeyboardEvent,
    PointerEvent,
    ReactNode,
    RefObject,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { VIEW_INPUT_EXCLUDED_PROPS } from '~/utils/viewInputExclusion';
import {
    CANVAS_BG,
    CHROME_BG,
    FOCUS_RING,
    HAIRLINE,
    READOUT,
    READOUT_DIM,
    READOUT_FONT,
} from '../chrome';
import {
    TITLE_BAR_DELIMITER_SX,
    TITLE_BAR_ICON_SX,
    TITLE_BAR_SX,
} from '../tile/TileTitleBar';
import {
    BoxSize,
    fitWidgetFrame,
    movedWidgetFrame,
    resizedWidgetFrame,
    WIDGET_ARROW_KEY_DIRECTIONS,
    WIDGET_KEY_STEP_LARGE_PX,
    WIDGET_KEY_STEP_PX,
    WIDGET_RESIZE_HANDLE_PX,
    WidgetFrame,
} from './widgetFrame';

/** Width kept for the subject's first letters and ellipsis, in CSS px. */
const SUBJECT_MIN_WIDTH_PX = 48;
/** Marks a title-bar item that takes the leftover width; others keep theirs. */
const FLEXIBLE_ITEM_ATTRIBUTE = 'data-title-bar-flexible';
const FLEXIBLE_ITEM_PROPS = { [FLEXIBLE_ITEM_ATTRIBUTE]: true };

const FOCUS_VISIBLE_SX = {
    '&:focus-visible': {
        outline: '1px solid',
        outlineColor: FOCUS_RING,
        outlineOffset: -1,
    },
};

type FrameChange = typeof movedWidgetFrame;

/** The width the title bar needs to show every item whole, in CSS px. */
function useTitleBarMinWidthPx(
    barRef: RefObject<HTMLElement | null>,
    hasSubject: boolean
): number {
    const [minWidthPx, setMinWidthPx] = useState(0);
    useLayoutEffect(() => {
        const bar = barRef.current;
        if (!bar) {
            return undefined;
        }
        const measure = () => {
            const style = getComputedStyle(bar);
            const items = Array.from(bar.children) as HTMLElement[];
            const fixedPx = items
                .filter((item) => !item.hasAttribute(FLEXIBLE_ITEM_ATTRIBUTE))
                .reduce((total, item) => total + item.offsetWidth, 0);
            const gapPx =
                (parseFloat(style.columnGap) || 0) * (items.length - 1);
            const paddingPx =
                (parseFloat(style.paddingLeft) || 0) +
                (parseFloat(style.paddingRight) || 0);
            const needed = Math.ceil(
                fixedPx +
                    gapPx +
                    paddingPx +
                    (hasSubject ? SUBJECT_MIN_WIDTH_PX : 0)
            );
            setMinWidthPx((current) => (current === needed ? current : needed));
        };
        measure();
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(measure);
        for (const item of Array.from(bar.children)) {
            if (!item.hasAttribute(FLEXIBLE_ITEM_ATTRIBUTE)) {
                observer.observe(item);
            }
        }
        return () => observer.disconnect();
    }, [barRef, hasSubject]);
    return minWidthPx;
}

interface FloatingWidgetProps {
    readonly title: string;
    /** What the widget reads. May carry PHI: masked by the privacy toggle. */
    readonly subject?: string;
    readonly actions?: ReactNode;
    /** Drawn fitted to {@link box}, never narrower than its title bar needs. */
    readonly frame: WidgetFrame;
    readonly box: BoxSize;
    /** Once when a pointer gesture ends; on each key, collapse or expand. */
    readonly onFrameChange: (frame: WidgetFrame) => void;
    readonly onClose: () => void;
    readonly children: ReactNode;
}

/**
 * A titled view floating over a box, moved and resized by pointer or arrow keys
 * and always kept inside the box. Input that starts within it never reaches the
 * views beneath, and its keys never move the grid's focus.
 */
export default function FloatingWidget({
    title,
    subject,
    actions,
    frame: placedFrame,
    box,
    onFrameChange,
    onClose,
    children,
}: FloatingWidgetProps) {
    const intl = useIntl();
    const titleBarRef = useRef<HTMLDivElement | null>(null);
    const minWidthPx = useTitleBarMinWidthPx(titleBarRef, Boolean(subject));
    // Drawn from during a pointer gesture; reported once, when it ends.
    const [gestureFrame, setGestureFrame] = useState<WidgetFrame | null>(null);
    const frame = fitWidgetFrame(gestureFrame ?? placedFrame, box, minWidthPx);
    const gestureRef = useRef<{
        readonly startX: number;
        readonly startY: number;
        readonly startFrame: WidgetFrame;
        readonly change: FrameChange;
        latest: WidgetFrame | null;
    } | null>(null);

    // No mouse events and pointer capture, so neither the view beneath nor a
    // control the gesture crosses hears it.
    const startGesture = (
        event: PointerEvent<HTMLElement>,
        change: FrameChange
    ) => {
        if (event.button !== 0) {
            return;
        }
        event.preventDefault();
        gestureRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            startFrame: frame,
            change,
            latest: null,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
    };
    const followGesture = (event: PointerEvent<HTMLElement>) => {
        const gesture = gestureRef.current;
        if (!gesture) {
            return;
        }
        gesture.latest = gesture.change(
            gesture.startFrame,
            event.clientX - gesture.startX,
            event.clientY - gesture.startY,
            box,
            minWidthPx
        );
        setGestureFrame(gesture.latest);
    };
    // Release, cancel and lost capture each end the gesture; the first one
    // reports it once and the others find no gesture.
    const endGesture = (event: PointerEvent<HTMLElement>) => {
        const gesture = gestureRef.current;
        gestureRef.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        if (!gesture) {
            return;
        }
        if (gesture.latest) {
            onFrameChange(gesture.latest);
        }
        setGestureFrame(null);
    };
    const gestureHandlers = (change: FrameChange) => ({
        onPointerDown: (event: PointerEvent<HTMLElement>) =>
            startGesture(event, change),
        onPointerMove: followGesture,
        onPointerUp: endGesture,
        onPointerCancel: endGesture,
        onLostPointerCapture: endGesture,
    });
    const stepBy =
        (change: FrameChange) => (event: KeyboardEvent<HTMLElement>) => {
            const delta = WIDGET_ARROW_KEY_DIRECTIONS[event.key];
            if (!delta) {
                return;
            }
            event.preventDefault();
            const stepPx = event.shiftKey
                ? WIDGET_KEY_STEP_LARGE_PX
                : WIDGET_KEY_STEP_PX;
            onFrameChange(
                change(
                    frame,
                    delta[0] * stepPx,
                    delta[1] * stepPx,
                    box,
                    minWidthPx
                )
            );
        };

    return (
        <Box
            {...VIEW_INPUT_EXCLUDED_PROPS}
            data-testid="floating-widget"
            role="group"
            aria-label={title}
            sx={{
                position: 'absolute',
                right: frame.rightPx,
                top: frame.topPx,
                width: frame.widthPx,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: CANVAS_BG,
                color: READOUT,
                border: 1,
                borderColor: HAIRLINE,
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: 8,
                pointerEvents: 'auto',
                fontVariantNumeric: 'tabular-nums',
            }}
        >
            <Box
                ref={titleBarRef}
                data-testid="floating-widget-title-bar"
                {...gestureHandlers(movedWidgetFrame)}
                sx={{ ...TITLE_BAR_SX, cursor: 'grab' }}
            >
                <Box
                    component="button"
                    type="button"
                    aria-label={intl.formatMessage(
                        { id: 'viewportGrid.widget.move' },
                        { title }
                    )}
                    data-testid="floating-widget-grip"
                    onKeyDown={stepBy(movedWidgetFrame)}
                    sx={{
                        ...TITLE_BAR_ICON_SX,
                        ...FOCUS_VISIBLE_SX,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        ml: '-5px',
                        border: 0,
                        bgcolor: 'transparent',
                        cursor: 'grab',
                    }}
                >
                    <GripVertical size={14} aria-hidden />
                </Box>
                <Box
                    component="span"
                    data-testid="floating-widget-title"
                    sx={{
                        color: READOUT,
                        fontFamily: READOUT_FONT,
                        fontSize: 11,
                        letterSpacing: '0.08em',
                        flexShrink: 0,
                    }}
                >
                    {title}
                </Box>
                {subject ? (
                    <>
                        <Box sx={TITLE_BAR_DELIMITER_SX} />
                        <Box
                            {...FLEXIBLE_ITEM_PROPS}
                            component="span"
                            data-testid="floating-widget-subject"
                            sx={{
                                color: READOUT_DIM,
                                fontFamily: READOUT_FONT,
                                fontSize: 11,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <SensitiveInfoTypography>
                                {subject}
                            </SensitiveInfoTypography>
                        </Box>
                    </>
                ) : null}
                <Box {...FLEXIBLE_ITEM_PROPS} sx={{ flex: 1 }} />
                {/* A press on a control is the control's: it never starts
                    a move, whose pointer capture would take its click. */}
                <Box
                    data-testid="floating-widget-controls"
                    sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}
                    onPointerDown={(event) => event.stopPropagation()}
                >
                    {actions}
                    <IconButton
                        size="small"
                        aria-label={intl.formatMessage(
                            {
                                id: frame.collapsed
                                    ? 'viewportGrid.widget.expand'
                                    : 'viewportGrid.widget.collapse',
                            },
                            { title }
                        )}
                        aria-expanded={!frame.collapsed}
                        data-testid="floating-widget-collapse"
                        sx={TITLE_BAR_ICON_SX}
                        onClick={() =>
                            onFrameChange({
                                ...frame,
                                collapsed: !frame.collapsed,
                            })
                        }
                    >
                        {frame.collapsed ? (
                            <ChevronRight size={14} />
                        ) : (
                            <ChevronDown size={14} />
                        )}
                    </IconButton>
                    <IconButton
                        size="small"
                        aria-label={intl.formatMessage(
                            { id: 'viewportGrid.widget.close' },
                            { title }
                        )}
                        data-testid="floating-widget-close"
                        sx={TITLE_BAR_ICON_SX}
                        onClick={onClose}
                    >
                        <X size={14} />
                    </IconButton>
                </Box>
            </Box>
            {frame.collapsed ? null : (
                <Box
                    data-testid="floating-widget-body"
                    sx={{
                        position: 'relative',
                        height: frame.heightPx,
                        overflow: 'hidden',
                    }}
                >
                    {children}
                    <Box
                        role="button"
                        tabIndex={0}
                        aria-label={intl.formatMessage(
                            { id: 'viewportGrid.widget.resize' },
                            { title }
                        )}
                        data-testid="floating-widget-resize"
                        onKeyDown={stepBy(resizedWidgetFrame)}
                        {...gestureHandlers(resizedWidgetFrame)}
                        sx={{
                            ...FOCUS_VISIBLE_SX,
                            position: 'absolute',
                            right: 0,
                            bottom: 0,
                            width: WIDGET_RESIZE_HANDLE_PX,
                            height: WIDGET_RESIZE_HANDLE_PX,
                            cursor: 'nwse-resize',
                            touchAction: 'none',
                            bgcolor: CHROME_BG,
                            clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                        }}
                    />
                </Box>
            )}
        </Box>
    );
}
